'use client';
import { requestGame } from '../lib/gameNavigation';

// ชิปสมาชิก + modal เข้าสู่ระบบ/สมัคร (พอร์ตจาก proj-sagame ปรับเป็น UFATHAI partner 918)
// ฝังใน Header ทุกหน้า — หน้าอื่นยัง server-render เหมือนเดิม มีแค่ก้อนนี้เป็น client island

import toast from '../lib/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LineIcon from './LineIcon';
import nextDynamic from 'next/dynamic';
import { THEME_URL, clearToken, getProfile, getToken, launchGame, login, loginMethod, refreshBalance, setToken , setRefCode} from '../lib/auth';

// สมัครสมาชิกเปิดเฉพาะตอนกดปุ่ม — แยกชิ้นออกจาก bundle หลักที่โหลดทุกหน้า (รวม /articles/*)
const RegisterForm = nextDynamic(() => import('./RegisterForm'), { ssr: false });

const LINE_URL = 'https://line.me/R/ti/p/%40usex';
const PIN_LEN = 6;

function normalizePhone(v) { return (v || '').replace(/\D/g, ''); }
function validPhone(v) { return /^0\d{8,9}$/.test(normalizePhone(v)); }

/* ลิงก์ LINE ดึงจาก GET /theme/{host} — แก้ที่หลังบ้านแล้วเว็บเปลี่ยนตาม */
function useBrandLine(fallback) {
  const [url, setUrl] = useState(fallback);
  useEffect(() => {
    let dead = false;
    fetch(THEME_URL).then((r) => r.json()).then((d) => {
      const line = (d.contact || []).find((c) => String(c.channel).toUpperCase() === 'LINE');
      if (!dead && line?.url) setUrl(line.url);
    }).catch(() => {});
    return () => { dead = true; };
  }, []);
  return url;
}

export default function AuthWidget() {
  const lineUrl = useBrandLine(LINE_URL);
  const [memberOpen, setMemberOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState('phone');
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const phoneRef = useRef(null);

  useEffect(() => {
    if (!memberOpen) return undefined;
    const away = (e) => { if (!e.target.closest('.member-chip')) setMemberOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMemberOpen(false); };
    document.addEventListener('click', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', away); document.removeEventListener('keydown', esc); };
  }, [memberOpen]);

  // เข้าเกม: รอ URL จริงก่อนเปิด พร้อม toast และป้องกันกดซ้ำ
  const enterGame = useCallback(async (gameName = 'casino') => {
    await requestGame(async (signal) => {
      const url = await launchGame(gameName, {}, signal);
      return url;
    });
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    // ⚠️ ล้าง token เฉพาะตอนหลังบ้านบอกว่าใช้ไม่ได้ (401/403)
    //    เดิม catch ทุก error แล้ว clearToken() — พอกดลิงก์ ?auth=...&next= แล้วหน้าเด้งไปทันที
    //    fetch จะถูกยกเลิกกลางคัน เข้า catch แล้วลบ token ทิ้ง = เหมือนโดนเตะออกจากระบบ
    getProfile().then(setProfile).catch((e) => { if (e?.status === 401 || e?.status === 403) clearToken(); });
  }, []);

  /* เปิดกล่องจากลิงก์ภายนอก (Rich menu LINE, โฆษณา ฯลฯ):
       https://ufathai.gay/?auth=login     → กล่องเข้าสู่ระบบ
       https://ufathai.gay/?auth=register  → กล่องสมัครสมาชิก
     ใช้ #login / #register ท้าย URL ก็ได้ · ถ้าล็อกอินอยู่แล้วจะไม่เด้ง */
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const ref = qp.get('ref');
    if (ref) setRefCode(ref.trim().toUpperCase());   // ลิ้งรับทรัพย์
    const raw = (qp.get('auth') || qp.get('modal') || window.location.hash.replace('#', '') || '').toLowerCase();
    const want = ['register', 'signup'].includes(raw) ? 'register'
      : ['login', 'signin'].includes(raw) ? 'login' : '';
    const next = qp.get('next');
    if (next && next.startsWith('/')) sessionStorage.setItem('ufathai-next', next);   // ไปต่อหลังล็อกอินสำเร็จ
    if (want && !getToken()) {
      sessionStorage.setItem('ufathai-from-link', '1');
      setMode(want); setStep('phone'); setError(''); setAuthOpen(true);
      qp.delete('auth'); qp.delete('modal'); qp.delete('next'); qp.delete('ref');   // กันเปิดซ้ำตอนกด back/refresh
      const qs = qp.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      return;
    }
    // ล็อกอินค้างอยู่แล้วกดลิงก์ ?auth= = ไม่ต้องเปิดกล่อง พาไปหน้าที่ขอ (ไม่ระบุ = หน้าแรก ซึ่งเป็นล็อบบี้เกม)
    if (want && getToken()) {
      sessionStorage.removeItem('ufathai-next');
      const to = next && next.startsWith('/') ? next : '/';
      if (to !== window.location.pathname) { window.location.replace(to); return; }
      qp.delete('auth'); qp.delete('modal'); qp.delete('next');
      const qs0 = qp.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs0 ? '?' + qs0 : ''));
      return;
    }
    if (sessionStorage.getItem('ufathai-open-auth')) {
      sessionStorage.removeItem('ufathai-open-auth');
      setStep('phone'); setAuthOpen(true);
    }
  }, []);

  // ล็อกอิน/สมัครสำเร็จแล้วมีปลายทางค้างอยู่ = พาไปหน้านั้น
  const goNext = useCallback(() => {
    const n = sessionStorage.getItem('ufathai-next');
    if (n && n.startsWith('/')) { sessionStorage.removeItem('ufathai-next'); window.location.href = n; return true; }
    // มาจากลิงก์ ?auth= โดยไม่ระบุปลายทาง = พาไปหน้าแรก (ล็อบบี้เกมหลังล็อกอิน)
    if (sessionStorage.getItem('ufathai-from-link')) {
      sessionStorage.removeItem('ufathai-from-link');
      if (window.location.pathname !== '/') { window.location.href = '/'; return true; }
    }
    return false;
  }, []);

  useEffect(() => {
    const onKeydown = (e) => { if (e.key === 'Escape') setAuthOpen(false); };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, []);

  useEffect(() => {
    const openAuth = (event) => {
      if (getToken()) return;
      setMode(event?.detail?.mode === 'register' ? 'register' : 'login');
      setStep('phone');
      setError('');
      setAuthOpen(true);
    };
    window.addEventListener('ufathai-open-auth', openAuth);
    return () => window.removeEventListener('ufathai-open-auth', openAuth);
  }, []);

  // ปุ่มที่มี data-auth ทุกที่ในเว็บ: ยังไม่ login = เปิด modal · login แล้ว = เข้าเกมเลย
  useEffect(() => {
    const onDocClick = (e) => {
      const trigger = e.target.closest('[data-auth]');
      if (!trigger) return;
      e.preventDefault();
      if (profile) { enterGame(trigger.dataset.game || 'casino'); return; }
      const wantRegister = trigger.dataset.auth === 'register' || /สมัคร/.test(trigger.textContent || '');
      setMode(wantRegister ? 'register' : 'login');
      setStep('phone'); setError(''); setAuthOpen(true);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [profile, enterGame]);

  const doLogin = useCallback(async (code) => {
    const phone = normalizePhone(phoneRef.current?.value);
    if (!validPhone(phone) || code.length !== PIN_LEN) return;
    setBusy(true); setError('');
    try {
      const { token } = await login(phone, code);
      setToken(token);
      window.cmsTrack?.('login_ok', '');
      toast.ok('เข้าสู่ระบบสำเร็จ', 'กำลังโหลดข้อมูลบัญชี…');
      getProfile().then(setProfile).catch(() => {});
      window.dispatchEvent(new Event('ufathai-auth'));
      setAuthOpen(false); setPin(''); setStep('phone');
      goNext();
    } catch (err) {
      const _m = err.message === 'record not found' ? 'ไม่พบบัญชีของเบอร์นี้' : err.message;
      setError(_m); toast.err('เข้าสู่ระบบไม่สำเร็จ', _m); setPin('');
    } finally { setBusy(false); }
  }, [goNext]);

  const pushDigit = useCallback((d) => {
    setError('');
    setPin((cur) => {
      if (cur.length >= PIN_LEN) return cur;
      const next = cur + d;
      if (next.length === PIN_LEN) queueMicrotask(() => doLogin(next));
      return next;
    });
  }, [doLogin]);
  const popDigit = useCallback(() => setPin((cur) => cur.slice(0, -1)), []);

  useEffect(() => {
    if (!authOpen || step !== 'pin') return undefined;
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') { e.preventDefault(); pushDigit(e.key); }
      else if (e.key === 'Backspace') { e.preventDefault(); popDigit(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [authOpen, step, pushDigit, popDigit]);

  async function onSubmit(e) {
    e.preventDefault(); setError('');
    const phone = normalizePhone(phoneRef.current?.value);
    if (!validPhone(phone)) { setError('กรอกเบอร์โทรศัพท์ให้ถูกต้อง (ขึ้นต้น 0)'); return; }
    if (step === 'pin') { doLogin(pin); return; }
    setBusy(true);
    try { await loginMethod(phone); setPin(''); setStep('pin'); }
    catch (err) {
      const _m = err.message === 'record not found' ? 'ไม่พบบัญชีของเบอร์นี้' : err.message;
      setError(_m); toast.err('เข้าสู่ระบบไม่สำเร็จ', _m);
    } finally { setBusy(false); }
  }

  async function handleRefresh(e) {
    e.preventDefault(); setBusy(true);
    const started = Date.now();
    try { await refreshBalance(); setProfile(await getProfile()); }
    catch (err) { setError(err.message); }
    finally {
      const left = 600 - (Date.now() - started);
      if (left > 0) await new Promise((r) => setTimeout(r, left));
      setBusy(false);
    }
  }

  function handleLogout(e) {
    e.preventDefault();
    clearToken(); setProfile(null);
    window.cmsTrack?.('logout', '');
    toast.info('ออกจากระบบแล้ว', 'แล้วพบกันใหม่');
    window.dispatchEvent(new Event('ufathai-auth'));
  }

  const credit = Number(profile?.credit || 0);

  return (
    <>
      <div className="member-box" id="memberBox">
        {!profile && (
        <div className="auth-actions">
          <button type="button" className="nav-cta" data-auth="register">สมัครสมาชิก</button>
          <button type="button" className="nav-login-btn" data-auth>เข้าสู่ระบบ</button>
        </div>
        )}
        {profile && (
        <div className={`member-chip${memberOpen ? ' open' : ''}`} id="memberChip" tabIndex={0}
          onClick={(e) => { if (!e.target.closest('.member-menu, .member-refresh')) setMemberOpen((v) => !v); }}>
          <span className="member-avatar">{profile?.phone?.charAt(0) || '-'}</span>
          <span className="member-info">
            <b>{profile?.phone || '-'}</b>
            <small>฿{credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</small>
          </span>
          <button type="button" className={`member-refresh${busy ? ' spin' : ''}`} onClick={handleRefresh}
            onMouseDown={(e) => e.preventDefault()} disabled={busy} title="รีเฟรชเครดิต" aria-label="รีเฟรชเครดิต">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" />
            </svg>
          </button>
          {memberOpen && typeof document !== 'undefined' && createPortal(
            <div className="member-sheet" onClick={(e) => { if (e.target.classList.contains('member-sheet')) setMemberOpen(false); }}>
              <div className="member-menu" onClick={(e) => { if (e.target.closest('a')) setMemberOpen(false); }}>
                <div className="mm-top">
                  <span>{profile?.phone || 'สมาชิก'}</span>
                  <button type="button" className="mm-close" aria-label="ปิดเมนู" onClick={() => setMemberOpen(false)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <path d="M6 6l12 12M18 6 6 18" />
                    </svg>
                  </button>
                </div>
                <div className="member-wallet">
                  <span>กระเป๋าหลัก</span>
                  <b>{credit.toLocaleString('th-TH', { minimumFractionDigits: 2 })} <small>THB</small></b>
                  <div className="member-wallet-sub">
                    <a href="/cashback"><span>คืนยอดเสีย</span><b>{Number(profile?.cashback ?? profile?.cashbackBalance ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} THB</b></a>
                    <a href="/wallet"><span>เครดิตในตู้เซฟ</span><b>{Number(profile?.creditSafe ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} THB</b></a>
                  </div>
                </div>
                <div className="mm-quick">
                  <a href="#" className="mm-play" onClick={(e) => { e.preventDefault(); enterGame('casino'); }}><LineIcon name="play" size={20} /> เข้าเล่นเกม</a>
                  <a href="/wallet?tab=deposit" className="mm-dep"><LineIcon name="plus" size={18} /> ฝากเงิน</a>
                </div>
                <div className="mm-scroll">
                  <div className="mm-h">กระเป๋าเงิน</div>
                  <a href="/wallet?tab=withdraw"><i><LineIcon name="withdraw" size={19} /></i> ถอนเงิน</a>
                  <a href="/wallet?tab=history"><i><LineIcon name="history" size={19} /></i> ประวัติฝาก-ถอน</a>
                  <a href="/bank"><i><LineIcon name="bank" size={19} /></i> จัดการบัญชีธนาคาร</a>
                  <div className="mm-h">สิทธิประโยชน์</div>
                  <a href="/promotions"><i><LineIcon name="gift" size={19} /></i> โปรโมชั่นทั้งหมด</a>
                  <a href="/promotions?tab=code"><i><LineIcon name="ticket" size={19} /></i> แลกโปรโมชั่นโค้ด</a>
                  <a href="/active-promotion"><i><LineIcon name="pin" size={19} /></i> โปรโมชั่นที่เข้าร่วม</a>
                  <a href="/cashback"><i><LineIcon name="cashback" size={19} /></i> คืนยอดเสีย</a>
                  <a href="/affiliate"><i><LineIcon name="link" size={19} /></i> ลิ้งรับทรัพย์</a>
                  <div className="mm-h">บัญชี &amp; ช่วยเหลือ</div>
                  <a href="/change-pin"><i><LineIcon name="lock" size={19} /></i> เปลี่ยนรหัส PIN</a>
                  <a href="/setting-dns"><i><LineIcon name="tool" size={19} /></i> วิธีแก้บล็อคเว็บเข้าไม่ได้</a>
                  <a href={lineUrl} target="_blank" rel="noopener"><i><LineIcon name="chat" size={19} /></i> ติดต่อเรา</a>
                </div>
                <a href="#" className="mm-out" onClick={handleLogout}><i><LineIcon name="logout" size={19} /></i> ออกจากระบบ</a>
              </div>
            </div>, document.body)}
        </div>
        )}
      </div>

      <div className={`auth-modal${authOpen ? ' open' : ''}`} id="authModal" role="dialog" aria-modal="true" aria-labelledby="authModalTitle"
        onClick={(e) => { if (e.target.id === 'authModal') setAuthOpen(false); }}>
        <div className="auth-card">
          <button className="auth-close" aria-label="ปิด" onClick={() => setAuthOpen(false)}>×</button>
          <div className="auth-head">
            <img className="auth-logo" src="/assets/logo-ufathai.svg" alt="UFATHAI" width="180" height="44" />
            <h3 id="authModalTitle">{mode === 'register' ? 'สมัครสมาชิก UFATHAI' : 'เข้าสู่ระบบ UFATHAI'}</h3>
            <p>{mode === 'register'
              ? 'สมัครง่ายๆ ไม่กี่ขั้นตอน สมัครเสร็จเข้าเล่นได้ทันที'
              : step === 'phone' ? 'กรอกเบอร์โทรศัพท์ที่สมัครไว้' : 'กรอก PIN 6 หลัก (กดที่แป้นหรือพิมพ์ก็ได้)'}</p>
          </div>
          {mode === 'register' ? (
            <RegisterForm
              onSwitchLogin={() => { setMode('login'); setError(''); }}
              onDone={async (token) => {
                setToken(token);
                const _p = await getProfile();
                setProfile(_p);
                window.dispatchEvent(new Event('ufathai-auth'));
                setAuthOpen(false); setMode('login');
                window.cmsTrack?.('signup_ok', _p?.username || '');
                goNext();
                toast.ok('สมัครสมาชิกสำเร็จ', _p?.username ? `ยูสเซอร์ของคุณคือ ${_p.username}` : 'เข้าสู่ระบบให้อัตโนมัติแล้ว', 4000);
              }}
            />
          ) : (
          <form className="auth-form" onSubmit={onSubmit}>
            <label style={step === 'pin' ? { display: 'none' } : undefined}>เบอร์โทรศัพท์
              <input ref={phoneRef} type="tel" name="phone" inputMode="tel" autoComplete="tel"
                placeholder="กรอกเบอร์ เช่น 0812345678"
                onInput={(ev) => { ev.currentTarget.value = ev.currentTarget.value.replace(/\D/g, '').slice(0, 10); }}
                readOnly={step === 'pin'} required />
            </label>
            {step === 'pin' && (
              <div className="pin-wrap">
                <div className="pin-dots" aria-label="รหัส PIN">
                  {Array.from({ length: PIN_LEN }).map((_, i) => (
                    <span key={i} className={`pin-dot${pin.length > i ? ' filled' : ''}${pin.length === i ? ' active' : ''}`} />
                  ))}
                </div>
                <div className="pin-pad">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0'].map((d) => (
                    d === ''
                      ? <span key="gap" className="pin-ghost" />
                      : <button type="button" key={d} onPointerDown={(ev) => { ev.preventDefault(); pushDigit(d); }} disabled={busy}>{d}</button>
                  ))}
                  <button type="button" className="pin-back" onPointerDown={(ev) => { ev.preventDefault(); popDigit(); }} disabled={busy} aria-label="ลบ">⌫</button>
                </div>
                <button type="button" className="pin-change" onClick={() => { setStep('phone'); setPin(''); setError(''); }}>เปลี่ยนเบอร์</button>
              </div>
            )}
            <p className="auth-error">{error}</p>
            {step === 'phone' && (
              <button type="submit" className="auth-btn-login auth-btn-full" disabled={busy}>
                {busy ? 'กำลังตรวจสอบ…' : 'ถัดไป'}
              </button>
            )}
            {step === 'pin' && busy && <p className="pin-busy">กำลังเข้าสู่ระบบ…</p>}
          </form>
          )}
          <div className="auth-divider"><span>{mode === 'register' ? 'ต้องการความช่วยเหลือ?' : 'ยังไม่มีบัญชี?'}</span></div>
          <div className="auth-foot">
            {mode === 'login' && (
              <button type="button" className="auth-btn-register" onClick={() => { setMode('register'); setError(''); }}>สมัครสมาชิก</button>
            )}
            <a className="auth-line" href={lineUrl} target="_blank" rel="noopener">
              <span className="auth-line-ico">LINE</span>
              <span>ติดต่อทีมงาน 24 ชม.</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
